/**
 * Model: Factory method that return a Class instance of Model and loads each instance with
 * common ORM methods for manipulating model data
 * DO NOT extend from model, only extend from an instance of model
 * eg. Person = new Model; Person.extend();
 */
import assign from 'object-assign';
import {makeCopy} from '@util/ObjectUtils';
import find from 'ramda/src/find';
import forEach from 'ramda/src/forEach';
import filter from 'ramda/src/filter';
import {hasLocalStorage} from 'util/StorageUtils';
import Class from './Class';
import Events from './Events';
import {client} from '@core/GraphQLProvider';
import guid from '@util/guid';

export default {
    extend: function ( prop ) {
        var Factory = Class.extend( assign( Events, {
            new_record: true,

            init: function ( attr ) {
                // shortcut to Model from instance
                this.parent = Model;
                if ( attr ) {
                    this.load( makeCopy( attr ) );
                    this.save();
                }
            },

            load: function ( attr ) {
                for ( var name in attr ) {
                    if ( attr.hasOwnProperty( name ) ) {
                        this[name] = attr[name];
                    }
                }
            },

            create: function () {
                if ( !this.id ) {
                    this.id = guid();
                }
                this.new_record = false;
                this.parent.records[this.id] = this.copy();
            },

            update: function () {
                this.parent.records[this.id] = this.copy();
            },

            save: function () {
                this.new_record ? this.create() : this.update();
            },

            copy: function () {
                return makeCopy( this );
            },

            set: function ( attr, value ) {

                if ( this.attributes.indexOf( attr ) ) {
                    this[attr] = value;

                    this.trigger( 'change:' + attr, this );
                }
            },

            attributes: function () {
                var result = {},
                    model_attr = this.parent.attributes;

                // return collection of attributes ONLY if the
                // attributes exists on the Model.attributes array
                result.id = this.id;
                for ( var i = 0, l = model_attr.length; i < l; i++ ) {
                    var attr = model_attr[i];
                    result[attr] = this[attr];
                }

                return result;
            },

            destroy: function () {
                delete this.parent.records[this.id];
            },

            toJSON: function () {
                return this.attributes();
            }

        } ) );

        //        assign( Factory, Events );
        let Model = Factory.extend( prop || {} );
        assign( Model, Events );

        Model.define_class_properties( {
            records: {},
            count: function () {
                var count = 0;
                for ( var key in this.records ) {
                    if ( this.records.hasOwnProperty( key ) ) {
                        count++;
                    }
                }
                return count;
            },

            // Also this could be added as a private property with getter/setters
            attributes: [],

            populate: function ( values ) {
                // reset records
                this.records = {};

                for ( var i = 0, l = values.length; i < l; i++ ) {
                    // create a new instance of this model and initialize it
                    var record = new this;
                    record.init( values[i] );
                }

                this.trigger( 'populated:model' );
                return this.records;
            },

            find: function ( id ) {
                const record = this.records[id];
                if ( !record ) {
                    return find( id, this.records );
                }

                return record.copy();
            },

            forEach: function ( fn, thisArg ) {
                if ( thisArg ) {
                    console.warn(
                        '[Deprecation Warning] Model:find called "forEach" supplying a context for "this" - must correct' );
                }
                // return forEach( this.records, fn, thisArg || this );
                return forEach( fn, this.records );
            },

            findByIndex: function ( index ) {
                var collection = this.all();
                return (collection[index]) ? collection[index] : null;
            },

            getIndex: function ( id ) {
                var records = this.records,
                    index = 0;

                for ( var key in records ) {
                    if ( id == records[key].id ) {
                        return index;
                    }
                    index++;
                }
                return -1;
            },

            all: function () {
                var records = this.records,
                    results = [];
                for ( var key in records ) {
                    results.push( records[key].copy() );
                }

                return results;
            },

            /**
             * http://lodash.com/docs#filter
             * @param opt
             * @returns {Array}
             */
            filter: function ( opt ) {
                return filter( opt, this.records );
            },

            destroy: function () {
                delete this.records;
            },

            save_local: function ( name ) {
                if ( !hasLocalStorage() ) {
                    return;
                }

                // convert Model.records to an array and serialize
                var result = [];

                for ( var key in this.records ) {
                    if ( this.records.hasOwnProperty( key ) ) {
                        result.push( this.records[key].toJSON() );
                    }
                }
                localStorage.setItem( name, JSON.stringify( result ) );
                return this;
            },

            load_local: function ( name ) {
                if ( !hasLocalStorage() ) {
                    return;
                }

                // load records from localStorage and populate model with data
                var result = JSON.parse( localStorage.getItem( name ) );
                if ( result ) {
                    this.populate( result );
                }

                return this;
            },

            load_remote: function () {
                if ( !this.remote_query ) {
                    throw new Error(
                        'upi.Model.load_remote: remote_query property must be defined to retrieve remote resource' );
                }

                return client.query( this.remote_query )
                    // return the root key of the response
                    .then( resp => {
                        const results = resp.data[Object.keys( resp.data )[0]];
                        this.populate( results );
                        return this;
                    } );
            },

            /**
             * Return an array of JSON objects containing the attributes for all records in this collection
             * @return {Array}
             */
            toJSON: function () {
                var records = this.all(),
                    results = [];

                $.each( records, function ( i, item ) {
                    results.push( item.toJSON() );
                } );

                return results;
            }

        } );

        return Model;
    }
};

